<template>
  <div class="evaluation-page theme-venue verify-page">
    <div class="ev-bg" aria-hidden="true">
      <span class="ev-wash"></span>
      <span class="ev-grid"></span>
    </div>

    <div class="ev-shell verify-shell">
      <div class="ev-brand">
        <img
          class="ev-logo"
          :src="logoSrc"
          width="96"
          height="68"
          alt="UGNAY — CHEDRO Caraga"
          decoding="async"
        />
        <span class="ev-brand-label">
          <span class="ev-brand-dot"></span>
          UGNAY · CHED Caraga
        </span>
      </div>

      <div v-if="loading" class="ev-card ev-state">
        <div class="spinner-border text-light"></div>
        <p class="mt-3 mb-0">Checking certificate…</p>
      </div>

      <div v-else-if="certificate" class="ev-card ev-state">
        <div class="state-icon success"><i class="bi bi-patch-check-fill"></i></div>
        <h5>Certificate verified</h5>
        <p>This certificate was issued by CHED Caraga.</p>
        <dl class="verify-facts">
          <dt>Issued to</dt>
          <dd>{{ certificate.recipient_name }}</dd>
          <dt v-if="certificate.activity_title">Activity</dt>
          <dd v-if="certificate.activity_title">{{ certificate.activity_title }}</dd>
          <dt>Date issued</dt>
          <dd>{{ formatDate(certificate.issued_at) }}</dd>
          <dt>Serial</dt>
          <dd class="verify-serial">{{ certificate.serial }}</dd>
        </dl>
      </div>

      <div v-else class="ev-card ev-state">
        <div class="state-icon error"><i class="bi bi-x-octagon-fill"></i></div>
        <h5>Certificate not found</h5>
        <p class="mb-0">
          No certificate matches this serial. If you were given this link,
          check it was copied in full, or email
          <a href="mailto:ccalinawan@ched.gov.ph">ccalinawan@ched.gov.ph</a>.
        </p>
      </div>

      <p class="ev-footer">CHED Caraga · Certificate verification</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "CertificateVerify",
  data() {
    return { loading: true, certificate: null };
  },
  computed: {
    logoSrc() {
      return `${process.env.BASE_URL}ugnay-logo-512.png`;
    },
  },
  mounted() {
    document.title = "Verify Certificate | CHED Caraga";
    this.verify();
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format("MMMM D, YYYY") : "";
    },
    async verify() {
      const serial = this.$route.params.serial;
      if (!serial) {
        this.loading = false;
        return;
      }
      try {
        const { data } = await axios.get(
          `api/certificate/verify/${encodeURIComponent(serial)}`
        );
        this.certificate = data && data.valid ? data : null;
      } catch (e) {
        // A 404 is the expected answer for an unknown serial, not an error to
        // report — the page's "not found" state IS the answer.
        this.certificate = null;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Reuses the UGNAY tokens from the evaluation page. Kept local rather than
   extracted: two pages is not yet a shared stylesheet. */
.evaluation-page {
  --ink: #f4f7fb;
  --ink-muted: rgba(244, 247, 251, 0.7);
  --ink-faint: rgba(244, 247, 251, 0.58);
  --gold: #e8a43a;
  --blue: #7eb8e8;
  --line: rgba(255, 255, 255, 0.1);
  --accent: #e8a43a;

  position: relative;
  min-height: 100vh;
  padding: 2rem 1rem 3rem;
  color: var(--ink);
  background: #0b1524;
  overflow: hidden;
}

.ev-bg { position: absolute; inset: 0; pointer-events: none; }

.ev-wash {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse 60% 50% at 20% 0%, rgba(29, 78, 216, 0.35), transparent 60%),
    radial-gradient(ellipse 50% 40% at 90% 20%, rgba(232, 164, 58, 0.18), transparent 55%),
    linear-gradient(180deg, #0f1d36 0%, #0b1524 100%);
}

.ev-grid {
  position: absolute;
  inset: 0;
  opacity: 0.12;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: radial-gradient(ellipse at center, #000 30%, transparent 75%);
  -webkit-mask-image: radial-gradient(ellipse at center, #000 30%, transparent 75%);
}

.verify-shell { max-width: 480px; margin: 0 auto; position: relative; z-index: 1; }

.ev-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.ev-logo {
  display: block;
  width: 96px;
  height: auto;
  filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.45));
}

.ev-brand-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--gold);
  text-transform: uppercase;
  letter-spacing: 0.14em;
}

.ev-brand-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--gold);
  box-shadow: 0 0 0 4px rgba(232, 164, 58, 0.18);
}

.ev-card {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid var(--line);
  border-radius: 18px;
  padding: 1.75rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.35);
}

.ev-state { text-align: center; color: var(--ink-muted); }
.ev-state h5 { color: var(--ink); font-weight: 700; }

.state-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  font-size: 1.75rem;
}

.state-icon.success { background: rgba(232, 164, 58, 0.16); color: var(--gold); }
.state-icon.error { background: rgba(229, 83, 83, 0.16); color: #fecaca; }

.verify-facts {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.35rem 0.9rem;
  margin: 1.25rem 0 0;
  text-align: left;
}

.verify-facts dt {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--ink-faint);
  padding-top: 0.15rem;
}

.verify-facts dd { margin: 0; color: var(--ink); font-weight: 600; }
.verify-serial { font-family: monospace; letter-spacing: 0.04em; }

.ev-card a { color: var(--blue); }

.ev-footer {
  text-align: center;
  font-size: 0.72rem;
  color: var(--ink-faint);
  margin: 1rem 0 0;
}
</style>
